import React, { useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Section, ErrorPanel, LoadingRow, EmptyNote, TableWrap, Th, Td, CapNote } from '../components/ui/bits';
import { useRpc, useSel } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { adminApi } from '../lib/adminApi';
import { agoLabel, fmtDate } from '../lib/format';

/** Derived stage, mirroring the app and marketing logic: dates first, never current_stage. */
function derivedStage(open?: string | null, deadline?: string | null): string {
  const today = new Date().toISOString().slice(0, 10);
  if (deadline && deadline < today) return 'closed';
  if (open && open <= today && (!deadline || deadline >= today)) return 'open';
  if (open && open > today) return 'opening soon';
  return 'tbc';
}

export const CataloguePage: React.FC = () => {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [revalidateOpen, setRevalidateOpen] = useState(false);
  const [revalidateResult, setRevalidateResult] = useState<string | null>(null);

  const { data: counts } = useSel<any[]>(['catalogue-counts'], async () => {
    const [firms, programmes, ukVis, usVis, ukDated, usDated, trackrUtm, noDomain] = await Promise.all([
      supabase.from('firms').select('*', { count: 'exact', head: true }),
      supabase.from('firm_programmes').select('*', { count: 'exact', head: true }).eq('is_visible', true),
      supabase.from('firm_programmes').select('*', { count: 'exact', head: true }).eq('is_visible', true).eq('locale', 'uk'),
      supabase.from('firm_programmes').select('*', { count: 'exact', head: true }).eq('is_visible', true).eq('locale', 'us'),
      supabase.from('firm_programmes').select('*', { count: 'exact', head: true }).eq('is_visible', true).eq('locale', 'uk').not('open_date', 'is', null),
      supabase.from('firm_programmes').select('*', { count: 'exact', head: true }).eq('is_visible', true).eq('locale', 'us').not('open_date', 'is', null),
      supabase.from('firm_programmes').select('*', { count: 'exact', head: true }).ilike('application_url', '%utm_medium=tracker%'),
      supabase.from('firms').select('*', { count: 'exact', head: true }).is('domain', null),
    ]);
    return { data: [{
      firms: firms.count, programmes: programmes.count, ukVis: ukVis.count, usVis: usVis.count,
      ukDated: ukDated.count, usDated: usDated.count, trackrUtm: trackrUtm.count, noDomain: noDomain.count,
    }], error: firms.error ?? programmes.error };
  });
  const c = (counts as any)?.[0];

  const { data: dupes } = useRpc<any[]>('admin_duplicate_firm_names');
  const { data: drafts } = useSel<any[]>(['firm-drafts'], () =>
    supabase.from('firm_drafts').select('id, status, scheduled_at, note, created_at').order('created_at', { ascending: false }).limit(15));
  const { data: history } = useSel<any[]>(['firm-history'], () =>
    supabase.from('firm_edit_history').select('action, actor_label, locale, created_at').order('created_at', { ascending: false }).limit(12));
  const { data: results, error, isLoading } = useSel<any[]>(['programme-search', search], () =>
    supabase.from('firm_programmes')
      .select('id, locale, programme_type, sub_sector, open_date, deadline, is_visible, application_url, firms!inner(name, tier)')
      .ilike('firms.name', `%${search}%`)
      .order('locale')
      .limit(50),
    { enabled: search.length >= 2 });

  return (
    <div>
      <PageHeader
        title="Firms and tracker"
        subtitle="Data quality, the programme browser and the publish levers. Editing stays in the app CMS."
        actions={
          <>
            <a href="https://app.intervyo.co.uk/admin-firms" target="_blank" rel="noreferrer">
              <Button size="sm">Open the CMS</Button>
            </a>
            <Button size="sm" variant="primary" onClick={() => setRevalidateOpen(true)}>Revalidate the tracker</Button>
          </>
        }
      />

      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 mb-4">
        <p className="text-[12.5px] text-amber-800">
          <span className="font-bold">Provenance hold:</span> the table firm_programmes_backup_20260817_trackr is the original Trackr import with provenance intact. It must not be deleted without legal advice; the backup sweeper on the Ops page refuses to touch it.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <StatTile label="Firms" value={c?.firms ?? 0} accent="indigo" />
        <StatTile label="Visible programmes" value={c?.programmes ?? 0} sub={`UK ${c?.ukVis ?? 0} · US ${c?.usVis ?? 0}`} accent="indigo" />
        <StatTile label="UK date coverage" value={c?.ukVis ? `${Math.round(((c?.ukDated ?? 0) / c.ukVis) * 100)}%` : '0%'} sub="programmes with an open date; alerts fire on dates" accent="amber" />
        <StatTile label="US date coverage" value={c?.usVis ? `${Math.round(((c?.usDated ?? 0) / c.usVis) * 100)}%` : '0%'} accent="amber" />
        <StatTile label="Trackr-era URLs" value={c?.trackrUtm ?? 0} sub="utm_medium=tracker, burn down by re-deriving" accent={c?.trackrUtm ? 'rose' : 'emerald'} />
        <StatTile label="Firms missing a domain" value={c?.noDomain ?? 0} accent="stone" />
      </div>

      <Section
        title="Programme browser"
        sub="Read-only. Stage is derived from dates; current_stage is deliberately not trusted. Edit in the CMS."
        className="mb-3"
        actions={
          <form onSubmit={e => { e.preventDefault(); setSearch(q.trim()); }} className="flex gap-2">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Firm name"
              className="h-8 w-56 px-2.5 rounded-md border border-stone-300 bg-white focus:border-indigo-400 focus:outline-none text-[12.5px]" />
            <Button size="sm" type="submit" variant="primary">Search</Button>
          </form>
        }
      >
        {isLoading && <LoadingRow />}
        {error && <ErrorPanel error={error} />}
        {!search && <EmptyNote>Search a firm to see its programmes</EmptyNote>}
        {search && !isLoading && (results ?? []).length === 0 && <EmptyNote>No programmes match</EmptyNote>}
        {(results ?? []).length > 0 && (
          <>
            <TableWrap>
              <thead><tr><Th>Firm</Th><Th>Locale</Th><Th>Programme</Th><Th>Opens</Th><Th>Deadline</Th><Th>Stage</Th><Th>Visible</Th><Th>Link</Th></tr></thead>
              <tbody>
                {(results ?? []).map(r => {
                  const stage = derivedStage(r.open_date, r.deadline);
                  return (
                    <tr key={r.id}>
                      <Td className="font-medium text-stone-900">{r.firms?.name}<span className="text-stone-400 font-normal"> {r.firms?.tier}</span></Td>
                      <Td>{r.locale}</Td>
                      <Td>{r.programme_type}{r.sub_sector ? ` · ${r.sub_sector}` : ''}</Td>
                      <Td>{fmtDate(r.open_date)}</Td>
                      <Td>{fmtDate(r.deadline)}</Td>
                      <Td><Badge tone={stage === 'open' ? 'emerald' : stage === 'opening soon' ? 'amber' : stage === 'closed' ? 'stone' : 'stone'}>{stage}</Badge></Td>
                      <Td>{r.is_visible ? 'yes' : <Badge tone="stone">hidden</Badge>}</Td>
                      <Td>{r.application_url ? <a className="text-indigo-600 hover:underline" href={r.application_url} target="_blank" rel="noreferrer">apply link</a> : 'none'}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </TableWrap>
            <CapNote shown={(results ?? []).length} cap={50} />
          </>
        )}
      </Section>

      <div className="grid md:grid-cols-3 gap-3">
        <Section title="Duplicate firm names" sub="Twins split bells and break the name-joined matcher. Merge in the CMS.">
          {(dupes ?? []).length === 0 ? <EmptyNote>None</EmptyNote> : (
            <div className="space-y-1 text-[12px]">
              {(dupes ?? []).map((d, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-stone-800 font-medium">{d.name}</span>
                  <span className="text-stone-400">{d.tier} · {d.programmes} programmes</span>
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section title="Draft queue" sub="The CMS publishes scheduled drafts every minute.">
          {(drafts ?? []).length === 0 ? <EmptyNote>No drafts</EmptyNote> : (
            <div className="space-y-1 text-[12px]">
              {(drafts ?? []).map(d => (
                <div key={d.id} className="flex justify-between gap-2">
                  <span className="text-stone-700 truncate">{d.note ?? d.id.slice(0, 8)}</span>
                  <span className="flex items-center gap-1.5">
                    <Badge tone={d.status === 'published' ? 'emerald' : d.status === 'scheduled' ? 'amber' : 'stone'}>{d.status}</Badge>
                    {d.scheduled_at && <span className="text-stone-400">{fmtDate(d.scheduled_at)}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section title="Recent catalogue edits">
          {(history ?? []).length === 0 ? <EmptyNote>No edits</EmptyNote> : (
            <div className="space-y-1 text-[12px]">
              {(history ?? []).map((h, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-stone-700">{h.action} <span className="text-stone-400">{h.locale} · {h.actor_label ?? ''}</span></span>
                  <span className="text-stone-400">{agoLabel(h.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <ConfirmDialog
        open={revalidateOpen} onClose={() => setRevalidateOpen(false)}
        title="Revalidate the tracker cache" confirmLabel="Revalidate"
        body={<>Purges the marketing tracker’s ISR cache so data writes show immediately instead of within the hour. Safe to run any time.</>}
        onConfirm={async () => {
          const r = await adminApi<{ ok: boolean; status: number }>('revalidate_tracker');
          setRevalidateResult(r.ok ? 'Tracker cache purged.' : `Failed with status ${r.status}`);
        }}
      />
      {revalidateResult && <p className="text-[12px] text-stone-500 mt-3">{revalidateResult}</p>}
    </div>
  );
};
